import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const dealMonitoringSlice = createSlice({
    name: "dealMonitoring",
    initialState: {
        page: 0,
        pageCount: 0,
        totalRow: 0,
        listData: null,
        statsData: null,
        detailData: null,
        dealPaymentsData: null,
        depositData: null,
        installmentData: null,
        exporting: false,
        errorDetailData: null,
    },
    reducers: {
        doGetDealListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.totalRow = action.payload.total_row;
        },
        doGetDealListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.totalRow = 0;
        },
        doGetDealStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetDealStatsFail: (state, action) => {
            state.statsData = null;
        },
        doGetDealDetailSuccess: (state, action) => {
            state.detailData = action.payload.deal;
        },
        doGetDealDetailFail: (state, action) => {
            state.detailData = null;
        },
        doGetDealPaymentsSuccess: (state, action) => {
            state.dealPaymentsData = action.payload;
        },
        doGetDealPaymentsFail: (state, action) => {
            state.dealPaymentsData = null;
        },
        doGetDepositReceiptSuccess: (state, action) => {
            state.depositData = action.payload.deposit;
        },
        doGetDepositReceiptFail: (state, action) => {
            state.depositData = null;
        },
        doGetInstallmentDetailSuccess: (state, action) => {
            state.installmentData = action.payload;
        },
        doGetInstallmentDetailFail: (state, action) => {
            state.installmentData = null;
        },
        resetInstallmentDetail: (state) => {
            state.installmentData = null;
        },
        setDealExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetDealMonitoringState: (state) => {
            state.detailData = null;
            state.dealPaymentsData = null;
            state.depositData = null;
            state.installmentData = null;
            state.errorDetailData = null;
        },
    },
});

const buildDealListUrl = (param) => {
    let url = `/api/admin/deal-monitoring?page=${param.page}&size=${param.limit}`;
    if (param.keyword) url += `&keyword=${encodeURIComponent(param.keyword)}`;
    if (param.status) url += `&status=${param.status}`;
    if (param.flow_type) url += `&flow_type=${param.flow_type}`;
    if (param.filter) url += `&filter=${param.filter}`;
    if (param.date_from) url += `&date_from=${param.date_from}`;
    if (param.date_to) url += `&date_to=${param.date_to}`;
    if (param.sort_by) url += `&sort_by=${param.sort_by}`;
    return url;
};

export const doGetDealList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(buildDealListUrl(param));
        dispatch(doGetDealListSuccess(response.data));
    } catch (error) {
        dispatch(doGetDealListFail(error.response));
    }
};

// ---- CSV export --------------------------------------------------------
// There is no server-side export endpoint for deals, so the whole filtered set
// is pulled through the normal list endpoint (which caps `size` at 100) and the
// CSV is assembled in the browser.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

const csvDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

// move_in_date + tenure_months - 1 day, clamped to the end of short months so it
// matches MySQL's DATE_ADD(..., INTERVAL ? MONTH) used elsewhere.
const csvTenancyEnd = (moveIn, months) => {
    if (!moveIn || !months) return "";
    const start = new Date(moveIn);
    if (isNaN(start)) return "";
    const target = new Date(start.getFullYear(), start.getMonth() + Number(months), 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(start.getDate(), lastDay));
    target.setDate(target.getDate() - 1);
    return csvDate(target);
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export const doExportDealMonitoring = (param) => async (dispatch) => {
    try {
        dispatch(setDealExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const url = buildDealListUrl({ ...param, page, limit: EXPORT_PAGE_SIZE });
            const response = await axios.get(url);
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Deal ID", "Listing", "Tenant", "Host", "Monthly Rent",
            "Tenancy Start", "Tenancy End", "Status", "Created Date",
        ];
        const body = rows.map((d) => [
            d.deal_code, d.listing_title, d.tenant_name, d.host_name, d.monthly_rent,
            csvDate(d.move_in_date), csvTenancyEnd(d.move_in_date, d.tenure_months),
            d.status, csvDate(d.created_at),
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const now = new Date();
        link.setAttribute("download", `deals_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setDealExporting(false));
    }
};

export const doGetDealStats = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/deal-monitoring/stats`);
        dispatch(doGetDealStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetDealStatsFail(error.response));
    }
};

export const doGetDealDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/deal-monitoring/${param.deal_id}`;
        const response = await axios.get(url);
        dispatch(doGetDealDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetDealDetailFail(error.response));
    }
};

export const doGetDealPayments = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/deal-monitoring/${param.deal_id}/payments`;
        const response = await axios.get(url);
        dispatch(doGetDealPaymentsSuccess(response.data));
    } catch (error) {
        dispatch(doGetDealPaymentsFail(error.response));
    }
};

export const doGetDepositReceipt = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/deal-monitoring/${param.deal_id}/deposit-receipt`;
        const response = await axios.get(url);
        dispatch(doGetDepositReceiptSuccess(response.data));
    } catch (error) {
        dispatch(doGetDepositReceiptFail(error.response));
    }
};

export const doGetInstallmentDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/deal-monitoring/${param.deal_id}/rent-payments/${param.rent_payment_id}/installment`;
        const response = await axios.get(url);
        dispatch(doGetInstallmentDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetInstallmentDetailFail(error.response));
    }
};

export const {
    doGetDealListSuccess, doGetDealListFail,
    doGetDealStatsSuccess, doGetDealStatsFail,
    doGetDealDetailSuccess, doGetDealDetailFail,
    doGetDealPaymentsSuccess, doGetDealPaymentsFail,
    doGetDepositReceiptSuccess, doGetDepositReceiptFail,
    doGetInstallmentDetailSuccess, doGetInstallmentDetailFail,
    resetInstallmentDetail,
    setDealExporting,
    resetDealMonitoringState,
} = dealMonitoringSlice.actions;

export const getDealList = (state) => state.dealMonitoring?.listData;
export const getDealStats = (state) => state.dealMonitoring?.statsData;
export const getDealListPage = (state) => state.dealMonitoring?.page;
export const getDealListPageCount = (state) => state.dealMonitoring?.pageCount;
export const getDealListTotalRow = (state) => state.dealMonitoring?.totalRow;
export const getDealDetail = (state) => state.dealMonitoring?.detailData;
export const getDealPaymentsData = (state) => state.dealMonitoring?.dealPaymentsData;
export const getDepositData = (state) => state.dealMonitoring?.depositData;
export const getInstallmentData = (state) => state.dealMonitoring?.installmentData;
export const getDealExporting = (state) => state.dealMonitoring?.exporting;

export default dealMonitoringSlice.reducer;
